import { Box, Group, Skeleton, Stack } from '@mantine/core';
import type { FloatingPosition } from '@mantine/core/lib/Floating';
import { useId } from '@mantine/hooks';
import { DEFAULT_ITEM_SIZE } from '@repo/common/components/MultiSelector/constants';
import MultiSelector from '@repo/common/components/MultiSelector/MultiSelector';
import type { ButtonVariants, IconNames } from '@repo/foundations';
import type { SecodaThemeShades } from '@repo/theme/types';
import React, { useCallback, useMemo } from 'react';
import { useAuthUser, useTagGroupList, useTagList } from '../../api';
import { extractInheritedTags } from '../../utils/extractInheritedTags';
import { closeAllModals, openModal } from '../ModalManager';
import IntegrationTooltipSelectorItem from '../RichTooltip/IntegrationTooltipSelectorItem';
import { TagOptionsModal } from '../Settings/TagOptionsModal';
import { TagModal } from '../Settings/TagSettings/Modal/TagModal';

interface TagPropertySelectorProps {
	onChange?: (value: string[]) => void;
	initialValue?: string[];
	placeholder?: string;
	placeholderIconName?: IconNames;
	customPropertyIdFilter?: string;
	forceVariant?: ButtonVariants;
	readOnly?: boolean;
	hideOnEmpty?: boolean;
	forceSourceTags?: boolean;
	entityIntegrationId?: string;
	field?: string;
	label?: string;
	withinPortal?: boolean;
	position?: FloatingPosition;
	defaultOpened?: boolean;
	noMaxHeightDropdown?: boolean;
}

function TagPropertySelector({
	noMaxHeightDropdown = false,
	onChange,
	initialValue,
	customPropertyIdFilter,
	placeholder = 'Tags',
	hideOnEmpty = false,
	placeholderIconName,
	forceVariant,
	readOnly = false,
	forceSourceTags,
	entityIntegrationId,
	field,
	label,
	withinPortal = true,
	position,
	defaultOpened = false,
}: TagPropertySelectorProps) {
	const uuid = useId();

	const { user } = useAuthUser();

	const [selected, setSelected] = React.useState<string[]>(initialValue ?? []);

	const { data: tags, isLoading: isTagsLoading } = useTagList({
		filters: customPropertyIdFilter
			? { custom_property_id: customPropertyIdFilter }
			: undefined,
	});

	const { data: tagGroups } = useTagGroupList({
		options: {
			select: ({ results }) => results,
		},
	});

	const memoizedTags = useMemo(
		() =>
			tags?.results
				.filter((tag) =>
					forceSourceTags ? initialValue?.includes(tag.id) : true
				)
				.map((tag) => ({
					label: tag.name,
					value: tag.id,
					color: tag.color as SecodaThemeShades,
					tagIntegration: tag.integration,
					maintainProp: forceSourceTags,
					entityIntegrationId: entityIntegrationId,
					itemTypeName: 'tag',
					description: tag.description,
					group:
						tagGroups?.find((tg) => tg.id === tag.tag_group_id)?.name ??
						'default',
				})),
		[
			tags?.results,
			entityIntegrationId,
			initialValue,
			forceSourceTags,
			tagGroups,
		]
	);

	// While the tag list is still loading, show a placeholder
	// option per known initial UUID
	const placeholderTags = useMemo(
		() =>
			(initialValue ?? []).map((id) => ({
				label: '',
				labelDetails: (
					<Skeleton animate visible height={10} width={64} radius="xl" />
				),
				value: id,
				color: 'gray' as SecodaThemeShades,
				maintainProp: forceSourceTags,
				entityIntegrationId: entityIntegrationId,
				itemTypeName: 'tag',
				group: 'default',
				hidden: true,
			})),
		[initialValue, forceSourceTags, entityIntegrationId]
	);

	// Use the placeholder list only while the real tag list is loading and
	// we don't yet have any results; once results arrive we always prefer
	// the real options.
	const hasTagResults = !!memoizedTags;
	const displayedOptions = useMemo(() => {
		if (isTagsLoading && !hasTagResults && (initialValue?.length ?? 0) > 0) {
			return placeholderTags;
		}
		return memoizedTags ?? [];
	}, [
		isTagsLoading,
		hasTagResults,
		initialValue,
		placeholderTags,
		memoizedTags,
	]);

	const allTags = useMemo(
		() => (tags?.results || []).map((t) => t.name),
		[tags?.results]
	);

	const inheritedTags = extractInheritedTags(memoizedTags, initialValue);

	// When the dropdown is opened while the tag list is still
	// loading, replace the default empty state with a stack of skeleton rows
	const loadingEmptyState = useMemo(
		() => (
			<Stack spacing="xs" py="xs" px="sm">
				{[0, 1, 2, 3, 4].map((i) => (
					<Group key={i} spacing="xs" noWrap>
						<Skeleton animate visible circle height={12} width={12} />
						<Skeleton
							animate
							visible
							height={10}
							width={`${55 + ((i * 37) % 40)}%`}
							radius="xl"
						/>
					</Group>
				))}
			</Stack>
		),
		[]
	);

	const handleChange = useCallback(
		(value: (string | boolean)[]) => {
			setSelected(value as string[]);
			onChange?.(value as string[]);
		},
		[onChange]
	);

	const handleCreate = useCallback(
		(value: string) => {
			if (customPropertyIdFilter) {
				openModal({
					title: 'Create option',
					children: (
						<TagOptionsModal
							key={`tag-modal-${uuid}`}
							customPropertyId={field?.split('.')[1]}
							propertyLabel={'option'}
						/>
					),
				});
				return;
			}

			openModal({
				title: 'Create tag',
				children: (
					<TagModal
						key={`tag-modal-${uuid}`}
						initialTitle={value}
						allTags={allTags}
						onClose={(newTag) => {
							if (newTag?.id) {
								const newSelected = [...selected, newTag.id];
								setSelected(newSelected);
								onChange?.(newSelected);
							}
							closeAllModals();
						}}
					/>
				),
			});
		},
		[customPropertyIdFilter, uuid, allTags, field, selected, onChange]
	);

	let variant: ButtonVariants =
		selected && selected.length > 0 ? 'default' : 'tertiary';

	if (forceVariant) {
		variant = forceVariant;
	}

	const selector = (
		<Box maw={256}>
			<MultiSelector
				hideOnEmpty={hideOnEmpty}
				placeholder={placeholder}
				placeholderIconName={placeholderIconName}
				variant={variant}
				iconType="tag"
				isViewerUser={false}
				options={displayedOptions}
				onChange={handleChange}
				initialSelected={selected}
				inheritedValues={forceSourceTags ? inheritedTags : undefined}
				property={label ?? 'tags'}
				permittedId={user.id}
				isMenuItemBadge={false}
				readOnly={readOnly}
				selectorItem={IntegrationTooltipSelectorItem}
				onCreate={handleCreate}
				withDescriptionTooltip
				withinPortal={withinPortal}
				position={position}
				defaultOpened={defaultOpened}
				noMaxHeightDropdown={noMaxHeightDropdown}
				virtuosoHeight={`min(70vh, ${(memoizedTags?.length || 8) * DEFAULT_ITEM_SIZE}px)`}
				emptyState={isTagsLoading ? loadingEmptyState : undefined}
			/>
		</Box>
	);

	return selector;
}

export default TagPropertySelector;
